import React from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight } from "@material-symbols-svg/react";

import Button from "../../Button";
import HomeHeader from "../HomeHeader/HomeHeader";

import { sendMessageToParent } from "../../../../helpers/common";
import { useAppSelector } from "src/redux/hooks";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import environment from "src/constants/environment";

interface HeroBannerProps {
  title: string;
  subtitle: string;
  description: string;
  type: string;
  background_image_url: string;
  background_color: string;
  logo_url: string;
  show_header_items: boolean;
  supportProgramKey: string;
}

const HeroBanner: React.FC<HeroBannerProps> = ({
  title,
  subtitle,
  description,
  type,
  background_image_url,
  background_color,
  logo_url,
  show_header_items,
  supportProgramKey,
}) => {
  const location = useLocation();
  const isProgramScreen = location.pathname.includes("program-screen");
  const patient = useAppSelector((state) => state.auth.patient);

  const handleBannerClick = () => {
    const data = {
      title: "Program Details",
      url: `${environment.DOMAIN_URL}/programs/${supportProgramKey}?mode=view_only`,
    };
    sendMessageToParent(POST_MESSAGE_EVENTS.NAVIGATE_TO_WEBVIEW, data);
  };

  const isPaidUser = Number(patient?.data?.paidUser) === 1;

  return (
    <div
      className="rounded-b-0 relative pb-2 flex flex-col items-center"
      style={{ backgroundColor: "var(--app-primary)" }}
    >
      {/* Static Floating Bubbles */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Large bubbles */}
        <div
          className="absolute top-4 left-8 w-16 h-16 rounded-full opacity-20"
          style={{
            background: `linear-gradient(135deg, var(--app-secondary), var(--app-secondary-light))`,
          }}
        />
        <div
          className="absolute top-12 right-12 w-12 h-12 rounded-full opacity-25"
          style={{
            background: `linear-gradient(45deg, var(--icon-bg-secondary), var(--app-secondary))`,
          }}
        />

        {/* Medium bubbles */}
        <div
          className="absolute bottom-8 left-16 w-10 h-10 rounded-full opacity-30"
          style={{
            background: `linear-gradient(90deg, var(--app-secondary-light), var(--icon-bg-secondary-light))`,
          }}
        />
        <div
          className="absolute top-20 left-1/3 w-8 h-8 rounded-full opacity-25"
          style={{
            background: `linear-gradient(180deg, var(--app-secondary), var(--banner-bg-start))`, // TODO: change to secondary
          }}
        />
        <div
          className="absolute bottom-12 right-8 w-10 h-10 rounded-full opacity-20"
          style={{
            background: `linear-gradient(225deg, var(--icon-bg-secondary), var(--app-secondary-light))`,
          }}
        />

        {/* Small bubbles */}
        <div
          className="absolute top-16 right-20 w-6 h-6 rounded-full opacity-35"
          style={{
            background: `linear-gradient(315deg, var(--app-secondary-light), var(--icon-bg-secondary-light))`,
          }}
        />
        <div
          className="absolute bottom-16 left-1/4 w-4 h-4 rounded-full opacity-30"
          style={{
            background: `linear-gradient(60deg, var(--app-secondary), var(--banner-bg-end))`, // TODO: change to secondary
          }}
        />
        <div
          className="absolute top-24 right-1/3 w-6 h-6 rounded-full opacity-25"
          style={{
            background: `linear-gradient(120deg, var(--icon-bg-secondary), var(--app-secondary))`,
          }}
        />
      </div>

      <HomeHeader supportProgramKey={supportProgramKey} />

      <div className="pt-8 px-4 pb-2">
        {/* Centered Logo */}
        <div className="flex justify-center mb-1.5">
          <div className="bg-white rounded-md px-4 py-2">
            <img
              src={logo_url || "/placeholder.svg"}
              alt="ActiLiv Logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>

        {/* Greeting Text - Centered */}
        {supportProgramKey === "anaya-care-program" ? (
          <div className="text-center mb-2">
            <h2
              className="text-2xl font-bold mb-0.5"
              style={{ color: "var(--hero-text-secondary)" }}
            >
              {isProgramScreen ? title : `Namaste, ${patient?.data?.name}!`}
            </h2>
            {!isProgramScreen && (
              <h1
                className="text-base font-normal mb-1.5"
                style={{ color: "var(--hero-text-secondary)" }}
              >
                {title}
              </h1>
            )}
            {isProgramScreen && (
              <p
                className="text-sm"
                style={{ color: "var(--hero-text-primary)" }}
              >
                {description}
              </p>
            )}
            {/* <p
              className="text-xs"
              style={{ color: "var(--hero-text-secondary)" }}
            >
              {description}
            </p> */}
          </div>
        ) : (
          <div className="text-center mb-2">
            <h1
              className="text-base font-bold mb-1.5"
              style={{ color: "var(--hero-text-secondary)" }}
            >
              Namaste, {patient?.data?.name}!
            </h1>
            <p
              className="text-xs"
              style={{ color: "var(--hero-text-secondary)" }}
            >
              {description}
            </p>
          </div>
        )}

        {/* Care Program Button */}
        {isPaidUser && (
          <div className="flex justify-center mt-6">
            <Button
              onClick={handleBannerClick}
              variant="contained"
              rounded="full"
              size="sm"
              color="white"
            >
              <span>Program Details</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroBanner;
